/**
 * Report Generator Hook
 * Custom hook for report generation with progress tracking
 */

import { useState, useCallback, useRef } from 'react';
import ReportGeneratorService from '../components/ReportGeneratorService';
import { REPORT_FORMATS, REPORT_STATUS } from '../types/reportTypes';

export const useReportGenerator = () => {
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(0);
  const [lastGenerated, setLastGenerated] = useState(null);
  const activeGenerationId = useRef(null);

  const generateReport = useCallback(async (reportType, data, options = {}) => {
    try {
      setGenerating(true);
      setError(null);
      setProgress(0);

      // Start generation
      const result = await ReportGeneratorService.generateReport(reportType, data, options);
      
      setLastGenerated(result);
      setProgress(100);
      
      return result;
    } catch (err) {
      console.error('Error generating report:', err);
      setError(err.message);
      return null;
    } finally {
      setGenerating(false);
      activeGenerationId.current = null;
    }
  }, []);

  const generateHTMLReport = useCallback(async (reportType, data, options = {}) => {
    return await generateReport(reportType, data, { 
      ...options, 
      format: REPORT_FORMATS.HTML 
    });
  }, [generateReport]);

  const generatePDFReport = useCallback(async (reportType, data, options = {}) => {
    return await generateReport(reportType, data, { 
      ...options, 
      format: REPORT_FORMATS.PDF 
    });
  }, [generateReport]);

  const generateCSVReport = useCallback(async (reportType, data, options = {}) => {
    return await generateReport(reportType, data, { 
      ...options, 
      format: REPORT_FORMATS.CSV 
    });
  }, [generateReport]);

  const generateExcelReport = useCallback(async (reportType, data, options = {}) => {
    return await generateReport(reportType, data, { 
      ...options, 
      format: REPORT_FORMATS.EXCEL 
    });
  }, [generateReport]);

  const generateJSONReport = useCallback(async (reportType, data, options = {}) => {
    return await generateReport(reportType, data, { 
      ...options, 
      format: REPORT_FORMATS.JSON 
    });
  }, [generateReport]);

  const cancelGeneration = useCallback(() => {
    if (activeGenerationId.current) {
      ReportGeneratorService.cancelGeneration(activeGenerationId.current);
      setGenerating(false);
      setProgress(0);
      activeGenerationId.current = null;
    }
  }, []);

  const getGenerationHistory = useCallback((limit = 10) => {
    return ReportGeneratorService.getGenerationHistory(limit);
  }, []);

  const getActiveGenerations = useCallback(() => {
    return ReportGeneratorService.getActiveGenerations();
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const clearLastGenerated = useCallback(() => {
    setLastGenerated(null);
  }, []);

  return {
    // State
    generating,
    error,
    progress,
    lastGenerated,

    // Actions
    generateReport,
    generateHTMLReport,
    generatePDFReport, 
    generateCSVReport,
    generateExcelReport,
    generateJSONReport,
    cancelGeneration,

    // Utilities
    getGenerationHistory,
    getActiveGenerations,
    clearError,
    clearLastGenerated,

    // Status helpers
    canGenerate: !generating,
    hasError: !!error,
    isComplete: progress === 100 && !generating
  };
};

// Specialized hook for batch report generation
export const useBatchReportGenerator = () => {
  const [batchGenerating, setBatchGenerating] = useState(false);
  const [batchResults, setBatchResults] = useState([]);
  const [batchError, setBatchError] = useState(null);
  const [batchProgress, setBatchProgress] = useState({ completed: 0, total: 0 });

  const generateBatchReports = useCallback(async (reportRequests) => {
    try {
      setBatchGenerating(true);
      setBatchError(null);
      setBatchResults([]);
      setBatchProgress({ completed: 0, total: reportRequests.length });

      const results = [];

      for (let i = 0; i < reportRequests.length; i++) {
        const request = reportRequests[i];
        
        try {
          const result = await ReportGeneratorService.generateReport(
            request.type,
            request.data,
            request.options || {}
          );
          
          results.push({ success: true, result });
        } catch (error) {
          results.push({ success: false, error: error.message, request });
        }

        setBatchProgress({ completed: i + 1, total: reportRequests.length });
      }

      setBatchResults(results);
      return results;
    } catch (err) {
      console.error('Error in batch report generation:', err);
      setBatchError(err.message);
      return [];
    } finally {
      setBatchGenerating(false);
    }
  }, []);

  const clearBatchResults = useCallback(() => {
    setBatchResults([]);
    setBatchProgress({ completed: 0, total: 0 });
  }, []);

  const clearBatchError = useCallback(() => {
    setBatchError(null);
  }, []);

  return {
    // State
    batchGenerating,
    batchResults,
    batchError,
    batchProgress,

    // Actions
    generateBatchReports,
    clearBatchResults,
    clearBatchError,

    // Status helpers
    canGenerateBatch: !batchGenerating,
    hasBatchError: !!batchError,
    batchComplete: batchProgress.completed === batchProgress.total && !batchGenerating
  };
};

// Hook for real-time generation monitoring
export const useGenerationMonitor = () => {
  const [activeGenerations, setActiveGenerations] = useState([]);

  const refreshActiveGenerations = useCallback(() => {
    const active = ReportGeneratorService.getActiveGenerations();
    setActiveGenerations(active);
  }, []);

  const monitorGeneration = useCallback((generationId, onProgress) => {
    const interval = setInterval(() => {
      const status = ReportGeneratorService.getGenerationStatus(generationId);
      
      if (status) {
        onProgress(status);
        
        if (status.status === REPORT_STATUS.COMPLETED || 
            status.status === REPORT_STATUS.FAILED ||
            status.status === REPORT_STATUS.CANCELLED) {
          clearInterval(interval);
        }
      } else {
        clearInterval(interval);
      }
    }, 500); // Check every 500ms

    return () => clearInterval(interval);
  }, []);

  return {
    activeGenerations,
    refreshActiveGenerations,
    monitorGeneration
  };
};